// @ts-check
// Modelo lógico de bloques del editor visual de diagramas (TSK-0021).
//
// Regla de arquitectura crítica (roadmap §7, ARC-R1; ver también
// backend/src/app/domain/ir.py): "la lógica debe almacenarse separada de la
// visualización". En consecuencia, este módulo define un modelo de "bloques"
// que refleja 1:1 los nodos de `backend/src/app/domain/ir.py` (mismo nombre
// de nodo en el campo `nodo`, mismos nombres de campo en español que las
// dataclasses de esa fuente de verdad) y añade ÚNICAMENTE un campo `id` de
// identidad estable por nodo, para que la capa visual
// (`editor-visual-state.js`) pueda indexar posición/tamaño sin que ese dato
// gráfico entre nunca en este módulo. Ningún campo de posición, tamaño o
// color existe aquí.
//
// Dirección de dependencia (DEC-012, docs/generated/decisions.md): este
// módulo NO importa `editor-visual-state.js` ni ningún módulo de vista/DOM.
// Es dato + funciones puras, reutilizable por palette-view.js, canvas-view.js,
// block-operations.js, connectors.js e ir-serialization.js (tareas futuras).

/** Discriminador de tipo de nodo: mismo nombre de clase que en ir.py. */
export const TIPO_NODO = Object.freeze({
  PROGRAMA: 'Programa',
  SECUENCIA: 'Secuencia',
  DECLARACION: 'Declaracion',
  ASIGNACION: 'Asignacion',
  ENTRADA: 'Entrada',
  SALIDA: 'Salida',
  COMENTARIO: 'Comentario',
  CONDICIONAL: 'Condicional',
  BUCLE_MIENTRAS: 'BucleMientras',
  BUCLE_FIN_PARA: 'BucleFinPara',
  REPETIR_HASTA: 'RepetirHasta',
  RETORNO: 'Retorno',
  LLAMADA: 'Llamada',
  PARAMETRO: 'Parametro',
  FUNCION: 'Funcion',
  LITERAL: 'Literal',
  REFERENCIA_VARIABLE: 'ReferenciaVariable',
  OPERACION: 'Operacion',
  ACCESO_INDICE: 'AccesoIndice',
  LISTA: 'Lista',
});

/** Tipos primitivos: mismos valores que `TipoPrimitivo` en ir.py. No son
 * nodos (no llevan `id`): en ir.py son un `Enum`, no una dataclass. */
export const TIPO_PRIMITIVO = Object.freeze({
  ENTERO: 'entero',
  DECIMAL: 'decimal',
  CADENA: 'cadena',
  BOOLEANO: 'booleano',
});

/** Operadores de `Operacion`: mismos valores que `Operador` en ir.py. */
export const OPERADOR = Object.freeze({
  SUMA: '+',
  RESTA: '-',
  MULTIPLICACION: '*',
  DIVISION: '/',
  MODULO: '%',
  IGUAL: '==',
  DISTINTO: '!=',
  MENOR: '<',
  MENOR_IGUAL: '<=',
  MAYOR: '>',
  MAYOR_IGUAL: '>=',
  Y: 'Y',
  O: 'O',
  NO: 'NO',
  NEGACION: 'NEG',
});

/** Operadores unarios (un único operando); el resto son binarios (dos). */
const OPERADORES_UNARIOS = /** @type {Set<string>} */ (new Set([OPERADOR.NO, OPERADOR.NEGACION]));

// --------------------------------------------------------------------------
// Identidad de nodo (SOLO identidad; nunca posición/tamaño/color)
// --------------------------------------------------------------------------

let contadorId = 0;

/**
 * Genera un identificador de nodo único y estable dentro de la sesión del
 * editor. No es una coordenada ni ningún otro dato visual: solo permite que
 * `editor-visual-state.js` referencie el nodo por id.
 * @param {string} prefijoTipo
 * @returns {string}
 */
function generarId(prefijoTipo) {
  contadorId += 1;
  return `${prefijoTipo}-${contadorId}`;
}

// --------------------------------------------------------------------------
// Validación mínima de argumentos de las factorías (errores explícitos)
// --------------------------------------------------------------------------

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {string}
 */
function requerirCadena(valor, nombreCampo) {
  if (typeof valor !== 'string' || valor.length === 0) {
    throw new TypeError(`${nombreCampo} debe ser una cadena no vacía`);
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {unknown[]}
 */
function requerirArray(valor, nombreCampo) {
  if (!Array.isArray(valor)) {
    throw new TypeError(`${nombreCampo} debe ser un array`);
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {NodoModelo}
 */
function requerirNodo(valor, nombreCampo) {
  if (!esNodo(valor)) {
    throw new TypeError(`${nombreCampo} debe ser un nodo del modelo (con "nodo" e "id")`);
  }
  return valor;
}

// --------------------------------------------------------------------------
// Tipos (JSDoc)
// --------------------------------------------------------------------------

/**
 * Forma común de todo nodo del modelo: los mismos campos que su nodo IR
 * equivalente en ir.py, más `id` (identidad, no visual).
 * @typedef {{ id: string, nodo: string } & Record<string, unknown>} NodoModelo
 */

/**
 * @param {unknown} valor
 * @returns {valor is NodoModelo}
 */
export function esNodo(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  const candidato = /** @type {Record<string, unknown>} */ (valor);
  return typeof candidato.nodo === 'string' && typeof candidato.id === 'string';
}

// --------------------------------------------------------------------------
// Factorías: Programa / Secuencia
// --------------------------------------------------------------------------

/**
 * @param {{ declaraciones?: NodoModelo[], funciones?: NodoModelo[], cuerpo?: NodoModelo }} [opciones]
 * @returns {NodoModelo}
 */
export function crearPrograma({ declaraciones = [], funciones = [], cuerpo } = {}) {
  return {
    id: generarId(TIPO_NODO.PROGRAMA),
    nodo: TIPO_NODO.PROGRAMA,
    declaraciones: requerirArray(declaraciones, 'declaraciones'),
    funciones: requerirArray(funciones, 'funciones'),
    cuerpo: cuerpo === undefined ? crearSecuencia() : requerirNodo(cuerpo, 'cuerpo'),
  };
}

/**
 * @param {NodoModelo[]} [sentencias]
 * @returns {NodoModelo}
 */
export function crearSecuencia(sentencias = []) {
  return {
    id: generarId(TIPO_NODO.SECUENCIA),
    nodo: TIPO_NODO.SECUENCIA,
    sentencias: requerirArray(sentencias, 'sentencias'),
  };
}

// --------------------------------------------------------------------------
// Factorías: sentencias
// --------------------------------------------------------------------------

/**
 * @param {{ nombre: string, tipo: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearDeclaracion({ nombre, tipo }) {
  return {
    id: generarId(TIPO_NODO.DECLARACION),
    nodo: TIPO_NODO.DECLARACION,
    nombre: requerirCadena(nombre, 'nombre'),
    tipo,
  };
}

/**
 * @param {{ variable: string, expresion: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearAsignacion({ variable, expresion }) {
  return {
    id: generarId(TIPO_NODO.ASIGNACION),
    nodo: TIPO_NODO.ASIGNACION,
    variable: requerirCadena(variable, 'variable'),
    expresion: requerirNodo(expresion, 'expresion'),
  };
}

/**
 * `tipo` (TSK-0212, DEC-074): tipo de dato esperado para `variable`,
 * OPCIONAL y retrocompatible -- mismo enum `TIPO_PRIMITIVO` ya usado por
 * `Parametro.tipo` (TSK-0211). Ausencia de `tipo` (`undefined`/no pasado,
 * el valor por defecto) es "sin tipo declarado": un diagrama guardado antes
 * de esta tarea deserializa exactamente igual que antes (ver
 * `ir-serialization.js`, que ya reconstruye cualquier campo extra de forma
 * genérica sin necesitar cambios). No se valida aquí que `tipo` pertenezca
 * a `TIPO_PRIMITIVO` (mismo criterio ya aplicado al resto de campos `tipo`
 * de este módulo, p.ej. `crearParametro`/`crearDeclaracion`: la validación
 * de forma/enum vive en `backend/src/app/domain/ir.py#_validar_tipo`, no en
 * esta factoría del editor).
 * @param {{ variable: string, tipo?: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearEntrada({ variable, tipo }) {
  return {
    id: generarId(TIPO_NODO.ENTRADA),
    nodo: TIPO_NODO.ENTRADA,
    variable: requerirCadena(variable, 'variable'),
    tipo,
  };
}

/**
 * @param {{ expresion: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearSalida({ expresion }) {
  return {
    id: generarId(TIPO_NODO.SALIDA),
    nodo: TIPO_NODO.SALIDA,
    expresion: requerirNodo(expresion, 'expresion'),
  };
}

/**
 * @param {{ texto: string }} campos
 * @returns {NodoModelo}
 */
export function crearComentario({ texto }) {
  return {
    id: generarId(TIPO_NODO.COMENTARIO),
    nodo: TIPO_NODO.COMENTARIO,
    texto: requerirCadena(texto, 'texto'),
  };
}

/**
 * @param {{ condicion: NodoModelo, ramaSi: NodoModelo, ramaSino?: NodoModelo | null }} campos
 * @returns {NodoModelo}
 */
export function crearCondicional({ condicion, ramaSi, ramaSino = null }) {
  return {
    id: generarId(TIPO_NODO.CONDICIONAL),
    nodo: TIPO_NODO.CONDICIONAL,
    condicion: requerirNodo(condicion, 'condicion'),
    rama_si: requerirNodo(ramaSi, 'rama_si'),
    rama_sino: ramaSino === null || ramaSino === undefined ? null : requerirNodo(ramaSino, 'rama_sino'),
  };
}

/**
 * @param {{ condicion: NodoModelo, cuerpo: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearBucleMientras({ condicion, cuerpo }) {
  return {
    id: generarId(TIPO_NODO.BUCLE_MIENTRAS),
    nodo: TIPO_NODO.BUCLE_MIENTRAS,
    condicion: requerirNodo(condicion, 'condicion'),
    cuerpo: requerirNodo(cuerpo, 'cuerpo'),
  };
}

/**
 * @param {{ variable: string, inicio: NodoModelo, fin: NodoModelo, paso: NodoModelo, cuerpo: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearBucleFinPara({ variable, inicio, fin, paso, cuerpo }) {
  return {
    id: generarId(TIPO_NODO.BUCLE_FIN_PARA),
    nodo: TIPO_NODO.BUCLE_FIN_PARA,
    variable: requerirCadena(variable, 'variable'),
    inicio: requerirNodo(inicio, 'inicio'),
    fin: requerirNodo(fin, 'fin'),
    paso: requerirNodo(paso, 'paso'),
    cuerpo: requerirNodo(cuerpo, 'cuerpo'),
  };
}

/**
 * @param {{ cuerpo: NodoModelo, condicion: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearRepetirHasta({ cuerpo, condicion }) {
  return {
    id: generarId(TIPO_NODO.REPETIR_HASTA),
    nodo: TIPO_NODO.REPETIR_HASTA,
    cuerpo: requerirNodo(cuerpo, 'cuerpo'),
    condicion: requerirNodo(condicion, 'condicion'),
  };
}

/**
 * @param {{ expresion?: NodoModelo | null }} [campos]
 * @returns {NodoModelo}
 */
export function crearRetorno({ expresion = null } = {}) {
  return {
    id: generarId(TIPO_NODO.RETORNO),
    nodo: TIPO_NODO.RETORNO,
    expresion: expresion === null || expresion === undefined ? null : requerirNodo(expresion, 'expresion'),
  };
}

/**
 * Llamada a función/procedimiento. Válida tanto en posición de sentencia
 * (roadmap §7 "Llamada a función"/"Subproceso") como de expresión (ir.py).
 *
 * `variableDestino` (TSK-0216, DEC-075): campo NUEVO y OPCIONAL, mismo
 * precedente exacto que `tipo` en `crearEntrada` (TSK-0212, DEC-074) --
 * propagado TAL CUAL (sin valor por defecto forzado: si no se pasa, la
 * propiedad `variable_destino` del nodo queda `undefined`, igual que
 * `Entrada.tipo` sin declarar) y sin validar aquí su forma/formato de
 * identificador (esa validación, cuando aplica, vive en la capa de UI que la
 * ofrezca -- `block-content-panel.js`, TSK-0216 -- no en esta factoría, mismo
 * criterio ya aplicado al resto de campos opcionales de este módulo). Un
 * diagrama guardado antes de esta tarea deserializa exactamente igual que
 * antes: `crearNodoGenerico`/`ir-serialization.js` no necesitan cambios (ver
 * cabecera del archivo).
 * @param {{ nombreFuncion: string, argumentos?: NodoModelo[], variableDestino?: string | null }} campos
 * @returns {NodoModelo}
 */
export function crearLlamada({ nombreFuncion, argumentos = [], variableDestino }) {
  return {
    id: generarId(TIPO_NODO.LLAMADA),
    nodo: TIPO_NODO.LLAMADA,
    nombre_funcion: requerirCadena(nombreFuncion, 'nombre_funcion'),
    argumentos: requerirArray(argumentos, 'argumentos'),
    variable_destino: variableDestino,
  };
}

// --------------------------------------------------------------------------
// Factorías: funciones y programa
// --------------------------------------------------------------------------

/**
 * @param {{ nombre: string, tipo: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearParametro({ nombre, tipo }) {
  return {
    id: generarId(TIPO_NODO.PARAMETRO),
    nodo: TIPO_NODO.PARAMETRO,
    nombre: requerirCadena(nombre, 'nombre'),
    tipo,
  };
}

/**
 * @param {{ nombre: string, parametros?: NodoModelo[], cuerpo?: NodoModelo, tipoRetorno?: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearFuncion({ nombre, parametros = [], cuerpo, tipoRetorno = null }) {
  return {
    id: generarId(TIPO_NODO.FUNCION),
    nodo: TIPO_NODO.FUNCION,
    nombre: requerirCadena(nombre, 'nombre'),
    parametros: requerirArray(parametros, 'parametros'),
    cuerpo: cuerpo === undefined ? crearSecuencia() : requerirNodo(cuerpo, 'cuerpo'),
    tipo_retorno: tipoRetorno,
  };
}

// --------------------------------------------------------------------------
// Factorías: expresiones
// --------------------------------------------------------------------------

/**
 * @param {{ valor: number | string | boolean, tipo: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearLiteral({ valor, tipo }) {
  return {
    id: generarId(TIPO_NODO.LITERAL),
    nodo: TIPO_NODO.LITERAL,
    valor,
    tipo,
  };
}

/**
 * @param {{ nombre: string }} campos
 * @returns {NodoModelo}
 */
export function crearReferenciaVariable({ nombre }) {
  return {
    id: generarId(TIPO_NODO.REFERENCIA_VARIABLE),
    nodo: TIPO_NODO.REFERENCIA_VARIABLE,
    nombre: requerirCadena(nombre, 'nombre'),
  };
}

/**
 * @param {{ operador: string, operandos: NodoModelo[] }} campos
 * @returns {NodoModelo}
 */
export function crearOperacion({ operador, operandos }) {
  requerirArray(operandos, 'operandos');
  const esperados = OPERADORES_UNARIOS.has(operador) ? 1 : 2;
  if (operandos.length !== esperados) {
    throw new TypeError(
      `operador '${operador}' espera ${esperados} operando(s) y recibió ${operandos.length}`
    );
  }
  return {
    id: generarId(TIPO_NODO.OPERACION),
    nodo: TIPO_NODO.OPERACION,
    operador,
    operandos,
  };
}

/**
 * @param {{ lista: NodoModelo, indice: NodoModelo }} campos
 * @returns {NodoModelo}
 */
export function crearAccesoIndice({ lista, indice }) {
  return {
    id: generarId(TIPO_NODO.ACCESO_INDICE),
    nodo: TIPO_NODO.ACCESO_INDICE,
    lista: requerirNodo(lista, 'lista'),
    indice: requerirNodo(indice, 'indice'),
  };
}

// --------------------------------------------------------------------------
// Factorías: tipos de dato
// --------------------------------------------------------------------------

/**
 * Tipo lista/colección homogénea (`Lista` en ir.py): a diferencia de
 * `TIPO_PRIMITIVO`, sí es un nodo (lleva `id`), igual que en ir.py.
 * @param {{ tipoElemento: unknown }} campos
 * @returns {NodoModelo}
 */
export function crearTipoLista({ tipoElemento }) {
  return {
    id: generarId(TIPO_NODO.LISTA),
    nodo: TIPO_NODO.LISTA,
    tipo_elemento: tipoElemento,
  };
}

// --------------------------------------------------------------------------
// Operaciones sobre Programa.funciones (TSK-0078: diagramas de función)
// --------------------------------------------------------------------------

/**
 * Añade `funcion` de forma inmutable a `programa.funciones` (patrón ya
 * establecido en este módulo, `{ ...programa, funciones: [...] }`, mismo
 * idioma que `insertarEnSecuencia`). No valida nombres duplicados: esa es
 * una decisión de UI (`editor-controller.js`), no una regla del modelo
 * lógico -- este módulo solo garantiza la forma inmutable de la operación.
 * @param {NodoModelo} programa
 * @param {NodoModelo} funcion
 * @returns {NodoModelo}
 */
export function agregarFuncion(programa, funcion) {
  if (programa.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('agregarFuncion requiere un nodo Programa');
  }
  requerirNodo(funcion, 'funcion');
  const funciones = /** @type {NodoModelo[]} */ (programa.funciones ?? []);
  return { ...programa, funciones: [...funciones, funcion] };
}

/**
 * Reemplaza de forma inmutable el `cuerpo` de la `Funcion` cuyo `id` es
 * `funcionId` dentro de `programa.funciones` (TSK-0078: pieza de
 * reconciliación del "Programa envoltorio" que usa `editor-controller.js`
 * para reutilizar `canvas-view.js`/`block-content-panel.js` -- ambos
 * "rutas_prohibidas" de esa tarea -- en el diagrama propio de una función
 * sin tocar ninguno de los dos). Si no existe ninguna función con ese `id`,
 * devuelve `programa` sin cambios (misma referencia), mismo idioma que
 * `reemplazarNodoPorId` de `block-content-panel.js` para "no encontrado".
 * @param {NodoModelo} programa
 * @param {string} funcionId
 * @param {NodoModelo} nuevoCuerpo
 * @returns {NodoModelo}
 */
export function actualizarCuerpoFuncion(programa, funcionId, nuevoCuerpo) {
  if (programa.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('actualizarCuerpoFuncion requiere un nodo Programa');
  }
  requerirNodo(nuevoCuerpo, 'nuevoCuerpo');
  const funciones = /** @type {NodoModelo[]} */ (programa.funciones ?? []);
  let huboCambio = false;
  const nuevasFunciones = funciones.map((funcion) => {
    if (funcion.id !== funcionId) return funcion;
    huboCambio = true;
    return { ...funcion, cuerpo: nuevoCuerpo };
  });
  if (!huboCambio) return programa;
  return { ...programa, funciones: nuevasFunciones };
}

/**
 * Reemplaza de forma inmutable `tipo_retorno` de la `Funcion` cuyo `id` es
 * `funcionId` dentro de `programa.funciones` (TSK-0217, DEC-076: cierra el
 * hueco de UI que dejaba `Funcion.tipo_retorno` permanentemente `null`,
 * pese a que el campo ya existe en el esquema desde antes de esta tarea y
 * `diagram_engine.py` ya lo consume correctamente, TSK-0215). Mismo patrón
 * inmutable EXACTO que `actualizarCuerpoFuncion` (mismo módulo, arriba):
 * "no encontrado" devuelve `programa` sin cambios (misma referencia), sin
 * lanzar. `nuevoTipoRetorno` acepta cualquiera de los valores de
 * `TIPO_PRIMITIVO` o `null` (sentinel "procedimiento, no devuelve nada" --
 * mismo significado que `null` en `crearFuncion({ tipoRetorno })` y que
 * "Sin declarar" en `Entrada.tipo`, TSK-0212): no se valida aquí que
 * pertenezca al enum, mismo criterio ya aplicado al resto de campos `tipo`/
 * `tipo_retorno` de este módulo (la validación de forma vive en
 * `backend/src/app/domain/ir.py#_validar_tipo`, no en esta factoría/
 * operación del editor).
 * @param {NodoModelo} programa
 * @param {string} funcionId
 * @param {unknown} nuevoTipoRetorno
 * @returns {NodoModelo}
 */
export function actualizarTipoRetornoFuncion(programa, funcionId, nuevoTipoRetorno) {
  if (programa.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('actualizarTipoRetornoFuncion requiere un nodo Programa');
  }
  const funciones = /** @type {NodoModelo[]} */ (programa.funciones ?? []);
  let huboCambio = false;
  const nuevasFunciones = funciones.map((funcion) => {
    if (funcion.id !== funcionId) return funcion;
    huboCambio = true;
    return { ...funcion, tipo_retorno: nuevoTipoRetorno };
  });
  if (!huboCambio) return programa;
  return { ...programa, funciones: nuevasFunciones };
}

/**
 * Elimina de forma inmutable la `Funcion` cuyo `id` es `funcionId` de
 * `programa.funciones` (TSK-0080, complemento simétrico de `agregarFuncion`/
 * `actualizarCuerpoFuncion`, TSK-0078: mismo patrón `{ ...programa,
 * funciones: [...] }`). Si no existe ninguna función con ese `id`, devuelve
 * `programa` sin cambios (misma referencia), mismo idioma de "no encontrado"
 * que `actualizarCuerpoFuncion`.
 *
 * No valida `Llamada`s existentes a la función eliminada en otros diagramas
 * (DEC-029, docs/generated/decisions.md): el motor de ejecución
 * (`diagram_engine.py`) ya falla de forma explícita en tiempo de ejecución
 * ante una `Llamada.nombre_funcion` que no resuelve contra ninguna
 * `Programa.funciones` -- comprobar llamadas rotas en el editor es una
 * validación de UI distinta, fuera del alcance mínimo de esta función (ver
 * ficha `tasks/*-TSK-0080-editor-delete-function.md`).
 * @param {NodoModelo} programa
 * @param {string} funcionId
 * @returns {NodoModelo}
 */
export function eliminarFuncion(programa, funcionId) {
  if (programa.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('eliminarFuncion requiere un nodo Programa');
  }
  const funciones = /** @type {NodoModelo[]} */ (programa.funciones ?? []);
  if (!funciones.some((funcion) => funcion.id === funcionId)) return programa;
  const nuevasFunciones = funciones.filter((funcion) => funcion.id !== funcionId);
  return { ...programa, funciones: nuevasFunciones };
}

// --------------------------------------------------------------------------
// Recorrido y utilidades genéricas del árbol
// --------------------------------------------------------------------------

/**
 * Recorre `nodo` y todos sus descendientes en preorden (equivalente en el
 * editor a `iterar_nodos` de ir.py, pero sobre el modelo con `id`).
 * @param {NodoModelo} nodo
 * @returns {Generator<NodoModelo>}
 */
export function* recorrerNodos(nodo) {
  yield nodo;
  for (const valor of Object.values(nodo)) {
    if (esNodo(valor)) {
      yield* recorrerNodos(valor);
    } else if (Array.isArray(valor)) {
      for (const elemento of valor) {
        if (esNodo(elemento)) yield* recorrerNodos(elemento);
      }
    }
  }
}

/**
 * @param {NodoModelo} raiz
 * @param {string} id
 * @returns {NodoModelo | undefined}
 */
export function buscarNodoPorId(raiz, id) {
  for (const nodo of recorrerNodos(raiz)) {
    if (nodo.id === id) return nodo;
  }
  return undefined;
}

/**
 * Busca la `Secuencia` cuyo array `sentencias` contiene directamente (sin
 * recursión adicional) un nodo con el id indicado. Utilidad de solo lectura
 * para que módulos futuros (p.ej. eliminar/reordenar bloques) localicen el
 * contenedor antes de operar con `eliminarDeSecuencia`/`moverEnSecuencia`.
 * @param {NodoModelo} raiz
 * @param {string} id
 * @returns {NodoModelo | undefined}
 */
export function buscarSecuenciaPadre(raiz, id) {
  for (const nodo of recorrerNodos(raiz)) {
    if (nodo.nodo === TIPO_NODO.SECUENCIA && Array.isArray(nodo.sentencias)) {
      if (nodo.sentencias.some((sentencia) => esNodo(sentencia) && sentencia.id === id)) {
        return nodo;
      }
    }
  }
  return undefined;
}

/**
 * @param {unknown} valor
 * @returns {unknown}
 */
function clonarConNuevosIds(valor) {
  if (Array.isArray(valor)) {
    return valor.map(clonarConNuevosIds);
  }
  if (esNodo(valor)) {
    /** @type {Record<string, unknown>} */
    const clon = { id: generarId(valor.nodo), nodo: valor.nodo };
    for (const [clave, val] of Object.entries(valor)) {
      if (clave === 'id' || clave === 'nodo') continue;
      clon[clave] = clonarConNuevosIds(val);
    }
    return clon;
  }
  return valor;
}

/**
 * Duplica un nodo y todo su subárbol asignando un `id` NUEVO a cada nodo
 * (incluidos los anidados): el original y la copia nunca comparten
 * identidad, para que borrar/mover una de las dos copias no afecte a la
 * otra (criterio de TSK-0024).
 * @param {NodoModelo} nodo
 * @returns {NodoModelo}
 */
export function duplicarNodo(nodo) {
  requerirNodo(nodo, 'nodo');
  return /** @type {NodoModelo} */ (clonarConNuevosIds(nodo));
}

// --------------------------------------------------------------------------
// Operaciones sobre Secuencia (actualizaciones disciplinadas/inmutables,
// docs/03-frontend.md §8.5: no se muta la secuencia recibida)
// --------------------------------------------------------------------------

/**
 * @param {NodoModelo} secuencia
 * @param {NodoModelo} sentencia
 * @param {number} [indice]
 * @returns {NodoModelo}
 */
export function insertarEnSecuencia(secuencia, sentencia, indice) {
  if (secuencia.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('insertarEnSecuencia requiere un nodo Secuencia');
  }
  requerirNodo(sentencia, 'sentencia');
  const sentenciasActuales = /** @type {NodoModelo[]} */ (secuencia.sentencias);
  const posicion = indice === undefined ? sentenciasActuales.length : indice;
  const sentencias = sentenciasActuales.slice();
  sentencias.splice(posicion, 0, sentencia);
  return { ...secuencia, sentencias };
}

/**
 * @param {NodoModelo} secuencia
 * @param {string} id
 * @returns {NodoModelo}
 */
export function eliminarDeSecuencia(secuencia, id) {
  if (secuencia.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('eliminarDeSecuencia requiere un nodo Secuencia');
  }
  const sentencias = /** @type {NodoModelo[]} */ (secuencia.sentencias).filter(
    (sentencia) => sentencia.id !== id
  );
  return { ...secuencia, sentencias };
}

/**
 * Reordena una sentencia dentro de la misma secuencia (el conector visual
 * mapea a este cambio de orden, TSK-0025; aquí solo se ofrece la primitiva
 * lógica).
 * @param {NodoModelo} secuencia
 * @param {string} id
 * @param {number} nuevoIndice
 * @returns {NodoModelo}
 */
export function moverEnSecuencia(secuencia, id, nuevoIndice) {
  if (secuencia.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('moverEnSecuencia requiere un nodo Secuencia');
  }
  const sentenciasActuales = /** @type {NodoModelo[]} */ (secuencia.sentencias);
  const indiceActual = sentenciasActuales.findIndex((sentencia) => sentencia.id === id);
  if (indiceActual === -1) {
    throw new RangeError(`no existe una sentencia con id '${id}' en la secuencia`);
  }
  const sentencias = sentenciasActuales.slice();
  const [sentencia] = sentencias.splice(indiceActual, 1);
  const indiceAcotado = Math.max(0, Math.min(nuevoIndice, sentencias.length));
  sentencias.splice(indiceAcotado, 0, sentencia);
  return { ...secuencia, sentencias };
}

// --------------------------------------------------------------------------
// Puente para ir-serialization.js (deserialización genérica)
// --------------------------------------------------------------------------

/**
 * Construye un nodo del modelo a partir de su tipo (nombre de nodo de
 * ir.py) y sus campos ya traducidos, asignándole un `id` nuevo. Pensado
 * para que `ir-serialization.js` reconstruya el modelo desde IR-JSON sin
 * duplicar la lógica de generación de ids (que permanece encapsulada aquí).
 * No valida reglas semánticas de cada tipo de nodo.
 * @param {string} tipoNodo
 * @param {Record<string, unknown>} campos
 * @returns {NodoModelo}
 */
export function crearNodoGenerico(tipoNodo, campos) {
  return { id: generarId(tipoNodo), nodo: tipoNodo, ...campos };
}
